import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config';

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  meta?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, string[]>;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${config.apiUrl}/api/${config.apiVersion}`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token && requestConfig.headers) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Methods

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post<ApiResponse<{ token: string; user: unknown }>>('/auth/login', { email, password }),

  logout: () => api.post('/auth/logout'),

  me: () => api.get<ApiResponse<unknown>>('/auth/me'),

  refreshToken: () => api.post<ApiResponse<{ token: string }>>('/auth/refresh'),
};

// Orders
export const ordersApi = {
  getAll: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    shipId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<ApiResponse<Order[]>>('/orders', { params }),

  getById: (id: string) => api.get<ApiResponse<OrderDetail>>(`/orders/${id}`),

  create: (data: CreateOrderInput) => api.post<ApiResponse<Order>>('/orders', data),
  updateStatus: (id: string, status: string) =>
    api.patch<ApiResponse<Order>>(`/orders/${id}/status`, { status }),
  verify: (id: string, verificationData: OrderVerificationInput) =>
    api.post<ApiResponse<Order>>(`/orders/${id}/verify`, verificationData),
};

// Inventory
export const inventoryApi = {
  getAll: (params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    zone?: string;
    search?: string;
    lowStock?: boolean;
  }) => api.get<ApiResponse<Product[]>>('/inventory', { params }),

  getById: (id: string) => api.get<ApiResponse<ProductDetail>>(`/inventory/${id}`),

  create: (data: CreateProductInput) => api.post<ApiResponse<Product>>('/inventory', data),

  update: (id: string, data: UpdateProductInput) =>
    api.patch<ApiResponse<Product>>(`/inventory/${id}`, data),

  adjustStock: (id: string, adjustment: StockAdjustmentInput) =>
    api.post<ApiResponse<Product>>(`/inventory/${id}/adjust`, adjustment),

  getLots: (productId: string) =>
    api.get<ApiResponse<InventoryLot[]>>(`/inventory/${productId}/lots`),

  getAllLots: (params?: { expiringWithin?: number; search?: string }) =>
    api.get<ApiResponse<InventoryLot[]>>('/inventory/lots', { params }),
};

// Warehouse Locations
export const warehouseApi = {
  getLocations: (params?: { zone?: string; available?: boolean }) =>
    api.get<ApiResponse<WarehouseLocation[]>>('/warehouse/locations', { params }),

  getLocationById: (id: string) =>
    api.get<ApiResponse<WarehouseLocation>>(`/warehouse/locations/${id}`),

  createLocation: (data: CreateLocationInput) =>
    api.post<ApiResponse<WarehouseLocation>>('/warehouse/locations', data),

  updateLocation: (id: string, data: UpdateLocationInput) =>
    api.patch<ApiResponse<WarehouseLocation>>(`/warehouse/locations/${id}`, data),
};

// Deliveries
export const deliveriesApi = {
  getAll: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    date?: string;
  }) => api.get<ApiResponse<Delivery[]>>('/deliveries', { params: {
    page: params?.page,
    limit: params?.pageSize,
    status: params?.status,
    scheduledDate: params?.date,
  } }),

  getById: (id: string) => api.get<ApiResponse<DeliveryDetail>>(`/deliveries/${id}`),

  // Delivery create and status writes were removed server-side (fail closed until an atomic
  // dispatch ledger exists; see apps/api/src/routes/deliveries.routes.ts). Do not re-add client
  // methods for them until the server routes return.

  update: (id: string, data: UpdateDeliveryInput) =>
    api.patch<ApiResponse<Delivery>>(`/deliveries/${id}`, data),

};

// Reports
export const reportsApi = {
  getDashboardKPIs: () => api.get<ApiResponse<DashboardKPIs>>('/reports/dashboard'),

  getOrdersTrend: (params?: { period?: string; startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<OrdersTrendData[]>>('/reports/orders-trend', { params }),

  getInventoryReport: () => api.get<ApiResponse<InventoryReport>>('/reports/inventory'),

  getDeliveryMetrics: () => api.get<ApiResponse<DeliveryMetrics>>('/reports/delivery-metrics'),
};

// Types
export interface Order {
  id: string;
  orderNumber: string;
  shipId: string;
  shipName: string;
  status: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  requestedDeliveryDate: string;
  totalItems: number;
  totalValue: number;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  unitPrice: number;
  pickedQuantity: number;
  lotNumber?: string;
}

export interface OrderDetail extends Order {
  items: OrderItem[];
  history: OrderHistoryEntry[];
}

export interface OrderHistoryEntry {
  id: string;
  status: string;
  timestamp: string;
  userId: string;
  userName: string;
  notes?: string;
}

export interface CreateOrderInput {
  shipId: string;
  shipName: string;
  priority: string;
  requestedDeliveryDate: string;
  items: { productId: string; quantity: number }[];
  notes?: string;
}

export interface UpdateOrderInput {
  priority?: string;
  requestedDeliveryDate?: string;
  notes?: string;
}

export interface OrderVerificationInput {
  items: { id: string; pickedQuantity: number; lotNumber?: string }[];
  notes?: string;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string;
  category: string;
  unit: string;
  unitPrice: number;
  allergens: string[];
  storageZone: string;
  minStock: number;
  maxStock: number;
  currentStock: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductDetail extends Product {
  lots: InventoryLot[];
  recentMovements: StockMovement[];
}

export interface InventoryLot {
  id: string;
  productId: string;
  productName?: string;
  productSku?: string;
  lotNumber: string;
  quantity: number;
  receivedDate: string;
  expiryDate: string;
  supplier: string;
  locationId: string;
  locationCode?: string;
}

export interface StockMovement {
  id: string;
  type: 'in' | 'out' | 'adjustment';
  quantity: number;
  reason: string;
  timestamp: string;
  userId: string;
  userName: string;
}

export interface CreateProductInput {
  sku: string;
  name: string;
  description?: string;
  category: string;
  unit: string;
  unitPrice: number;
  allergens: string[];
  storageZone: string;
  minStock: number;
  maxStock: number;
}

export interface UpdateProductInput {
  name?: string;
  description?: string;
  category?: string;
  unitPrice?: number;
  allergens?: string[];
  minStock?: number;
  maxStock?: number;
  isActive?: boolean;
}

export interface StockAdjustmentInput {
  quantity: number;
  type: 'add' | 'remove' | 'set';
  reason: string;
  lotNumber?: string;
}

export interface WarehouseLocation {
  id: string;
  code: string;
  zone: string;
  aisle: string;
  rack: string;
  shelf: string;
  capacity: number;
  currentUsage: number;
  isActive: boolean;
  createdAt: string;
}

export interface CreateLocationInput {
  code: string;
  zone: string;
  aisle: string;
  rack: string;
  shelf: string;
  capacity: number;
}

export interface UpdateLocationInput {
  capacity?: number;
  isActive?: boolean;
}

export interface Delivery {
  id: string;
  orderId: string;
  orderNumber: string;
  deliveryNumber: string;
  shipName: string;
  status: string;
  scheduledDate: string;
  actualDate?: string;
  vehicleId?: string;
  driverName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryDetail extends Delivery {
  order: Order;
  items: OrderItem[];
}

export interface CreateDeliveryInput {
  orderId: string;
  scheduledDate: string;
  vehicleId?: string;
  driverName?: string;
  notes?: string;
}

export interface UpdateDeliveryInput {
  scheduledDate?: string;
  vehicleId?: string;
  driverName?: string;
  notes?: string;
}

export interface DashboardKPIs {
  totalOrders: number;
  ordersChange: number;
  pendingOrders: number;
  pendingChange: number;
  todayDeliveries: number;
  deliveriesChange: number;
  inventoryValue: number;
  inventoryChange: number;
  lowStockItems: number;
  expiringItems: number;
}

export interface OrdersTrendData {
  date: string;
  orders: number;
  deliveries: number;
  value: number;
}

export interface InventoryReport {
  totalProducts: number;
  totalValue: number;
  byCategory: { category: string; count: number; value: number }[];
  byZone: { zone: string; count: number; utilization: number }[];
  lowStockProducts: Product[];
  expiringLots: InventoryLot[];
}

export interface DeliveryMetrics {
  totalDeliveries: number;
  onTimeRate: number;
  averageDeliveryTime: number;
  byStatus: { status: string; count: number }[];
  byDay: { day: string; count: number }[];
}

export default api;

// ============================================================================
// MarineX Cloud
// ============================================================================

export type MarineXShipper = 'PCL' | 'TRANSCOR' | 'HORIZON' | 'PIER';
export type MarineXMasterProfile = 'CCL' | 'PRINCESS' | 'HOLLAND' | 'SEABOURN' | 'WINDSTAR' | 'NCL';
export type MarineXCruiseLineCode = 'CCL' | 'PCL' | 'HAL' | 'SBN' | 'WDS' | 'NCL';

export interface MarineXCruiseLine {
  name: string;
  code: MarineXCruiseLineCode;
  master: MarineXMasterProfile;
  defaultShipper: MarineXShipper;
}

export interface MarineXWorkOrderLine {
  lineNumber: number;
  cost: number | null;
  breakdown: string;
  quantity: number;
  uom: string;
  itemNumber: string;
  description: string;
  unitPrice: number | null;
  extendedPrice: number | null;
  cartonCount: number;
  boxGroup: string | null;
  weight: number | null;
  status: string;
  matchedBy: string | null;
  notes: string;
}

export interface MarineXWorkOrder {
  cruiseLine: MarineXCruiseLine;
  ship: string | null;
  poNumber: string | null;
  voyageNumber: string | null;
  orderDate: string | null;
  deliveryDate: string | null;
  shipper: MarineXShipper | null;
  shipperReason: string;
  status: string;
  lines: MarineXWorkOrderLine[];
  totals: { cartons: number; totalValue: number; weightLbs: number };
  issues: MarineXWorkOrderLine[];
}

export interface MarineXPriceCheckResult {
  status: string;
  matched: unknown | null;
  matchedBy: string | null;
  masterPrice: number | null;
  importedPrice: number | null;
  message: string;
}

export interface MarineXRoutingResult {
  shipper: MarineXShipper | null;
  reason: string;
  matchedRule: boolean;
  needsReview: boolean;
}

export interface MarineXBuildWorkOrderInput {
  cruiseLineCode?: MarineXCruiseLineCode;
  company?: string;
  preliminaryRows?: unknown[][];
  preliminaryText?: string;
  master?: { profile: MarineXMasterProfile; rows?: unknown[][] };
  routingText?: string | null;
}

export type MarineXExportType =
  | 'bartender-csv'
  | 'exception-report'
  | 'packing-summary'
  | 'work-order-csv'
  | 'warehouse-copy-csv';

export const marinexApi = {
  getCruiseLines: () =>
    api.get<ApiResponse<MarineXCruiseLine[]>>('/marinex/cruise-lines'),

  importPreliminary: (body: { rows?: unknown[][]; text?: string }) =>
    api.post<ApiResponse<unknown>>('/marinex/import', body),

  buildWorkOrder: (body: MarineXBuildWorkOrderInput) =>
    api.post<ApiResponse<MarineXWorkOrder>>('/marinex/work-order', body),

  priceCheck: (body: {
    profile: MarineXMasterProfile;
    itemNumber: string;
    uom: string;
    unitPrice?: number | null;
    masterRows?: unknown[][];
  }) => api.post<ApiResponse<MarineXPriceCheckResult>>('/marinex/price-check', body),

  detectRouting: (body: {
    text: string | null;
    cruiseLineCode?: MarineXCruiseLineCode;
    company?: string;
  }) => api.post<ApiResponse<MarineXRoutingResult>>('/marinex/routing', body),

  exportWorkOrder: (body: MarineXBuildWorkOrderInput & { type: MarineXExportType }) =>
    api.post<ApiResponse<{ type: MarineXExportType; body: string }>>('/marinex/export', body),

  saveWorkOrder: (body: MarineXBuildWorkOrderInput) =>
    api.post<ApiResponse<MarineXStoredWorkOrder>>('/marinex/work-orders', body),

  listWorkOrders: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<ApiResponse<MarineXStoredOrderRow[]>>('/marinex/work-orders', { params }),

  getWorkOrder: (id: string) =>
    api.get<ApiResponse<MarineXStoredWorkOrder>>(`/marinex/work-orders/${id}`),

  updateWorkOrderStatus: (id: string, status: string) =>
    api.patch<ApiResponse<MarineXStoredWorkOrder>>(`/marinex/work-orders/${id}/status`, { status }),

  recordOverride: (
    id: string,
    body: { fieldChanged: string; oldValue?: string; newValue?: string; reason?: string; orderLineId?: string; apply?: boolean },
  ) => api.post<ApiResponse<unknown>>(`/marinex/work-orders/${id}/overrides`, body),
};

export interface MarineXStoredWorkOrder extends MarineXWorkOrder {
  id: string;
  orderNumber: string;
}

export interface MarineXStoredOrderRow {
  id: string;
  order_number: string;
  po_number: string | null;
  voyage_number: string | null;
  shipper: MarineXShipper | null;
  status: string;
  total_cartons: number;
  total_value: number;
  total_weight_lbs: number;
  created_at?: string;
}
